import React, { useEffect, useMemo, useState } from 'react';
import {
  AppButton,
  Box,
  CardTypesEnum,
  Inscription,
  ShimmerBox,
  Text,
  useDebounce,
} from '@common-fe/common-fe';

import { ModalWrapper } from '@/components/wrappers';
import PlanTypeIcon from '@/modules/plan/PlanSetup/Sidebar/PlanTypeIcon/PlanTypeIcon';
import colors from '@/styles/colors';

import type { EligibleServiceCategory } from './hooks/useEligibleServicesFormatter';
import useEligibleServicesFormatter from './hooks/useEligibleServicesFormatter';
import useEligibleServicesModal from './hooks/useEligibleServicesModal';
import { useGetServicesByAiQuery } from './queries/useGetSericesByAi.query';
import { EligibleServicesAiSearch } from './EligibleServicesAiSearch';
import EligibleServicesCategoryGroup from './EligibleServicesCategoryGroup';

interface Props {
  categories: EligibleServiceCategory[];
  accountName?: string;
  accountType?: string;
  policyId?: string | number;
}

const EligibleServicesModal: React.FC<Props> = ({ categories, accountName, accountType, policyId }) => {
  const { visible, handleClose } = useEligibleServicesModal();
  const [searchString, setSearchString] = useState('');
  const debouncedSearch = useDebounce(searchString, 500);

  useEffect(() => {
    if (!visible) {
      setSearchString('');
    }
  }, [visible]);

  const { data, isLoading } = useGetServicesByAiQuery({
    policyIds: policyId ? [policyId] : undefined,
    searchString: debouncedSearch,
  });

  const formattedData = useEligibleServicesFormatter(data);

  const currentCategories = useMemo(() => {
    if (debouncedSearch && searchString.length >= 3) return formattedData.categories;
    return categories;
  }, [formattedData.categories, categories, debouncedSearch, searchString]);

  return (
    <ModalWrapper testId="eligible-services-modal" visible={visible} onSetVisible={handleClose}>
      <Box data-testid="eligible-services-modal_content">
        <Box align="center" gap="spacing8">
          <PlanTypeIcon
            type={accountType as CardTypesEnum}
            size="32px"
            color={colors.cardTypes[accountType as CardTypesEnum]}
          />
          <Text
            size="2xl"
            weight="bold"
            color="textBody"
            textAlign="center"
            data-testid="eligible-services-modal_title"
          >
            {`${accountName || ''} Eligible Services`}
          </Text>
        </Box>
        <Text size="medium" color="textBody" textAlign="center">
          You cannot select a service here. Services are automatically matched based on your claim description
          during the submission flow.
        </Text>
        <Box
          background="module"
          round="moduleRound"
          pad="spacing24"
          gap="spacing24"
          margin={{ vertical: 'spacing24' }}
        >
          <Box>
            <EligibleServicesAiSearch searchString={searchString} setSearchString={setSearchString} />
          </Box>
          {isLoading ? (
            <ShimmerBox width="100%" height="500px" />
          ) : (
            <Box>
              {!currentCategories.length ? (
                <Inscription data-testid="eligible-services-modal_inscription">
                  No services found. Please try adjusting your entry or using broader terms to find a match.
                </Inscription>
              ) : (
                <Box gap="spacing16">
                  {currentCategories.map((category) => (
                    <EligibleServicesCategoryGroup key={category.id} category={category} />
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Box>
        <Box align="end">
          <AppButton
            testId="eligible-services-modal_close-button"
            buttonType="secondary"
            onClick={handleClose}
            width="150px"
          >
            Close
          </AppButton>
        </Box>
      </Box>
    </ModalWrapper>
  );
};

export default EligibleServicesModal;
